import { useEffect, useState } from 'react'
import { Mail, MessagesSquare, Repeat, Send, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { LucideIcon } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import { GtmSpinner } from '@/features/gtm/components/gtm-spinner'
import {
  useInView,
  usePrefersReducedMotion,
} from '@/features/gtm/components/use-in-view'
import { GtmStageBadge } from '@/features/gtm/workspace/gtm-stage-badge'
import { cn } from '@/shared/lib/utils'

/**
 * The conversation plays as a timeline: each event waits its delay (ms)
 * after the one before. An email is "pending" (sending, or a reply
 * arriving) for a moment before it shows.
 */
const TIMELINE = [
  { event: 'first_pending', delay: 300 },
  { event: 'first', delay: 900 },
  { event: 'follow_up_pending', delay: 1100 },
  { event: 'follow_up', delay: 900 },
  { event: 'reply_pending', delay: 1100 },
  { event: 'reply', delay: 1200 },
  { event: 'drafting', delay: 900 },
  { event: 'typing', delay: 1000 },
] as const

type TimelineEvent = (typeof TIMELINE)[number]['event']

/** How fast the AI draft types itself, in ms per character. */
const TYPE_MS = 18

const reached = (step: number, event: TimelineEvent) =>
  step > TIMELINE.findIndex((item) => item.event === event)

/** Spinner line shown in an email's place while it is on its way. */
function Pending({ label }: { label: string }) {
  return (
    <div className="absolute inset-x-0 top-4 flex items-center gap-2 text-xs text-muted-foreground motion-safe:animate-in motion-safe:fade-in">
      <GtmSpinner className="size-3.5" />
      {label}
    </div>
  )
}

/**
 * One email of the thread, laid out as the workspace's lead sheet shows it.
 * It keeps its place while hidden, so the card does not grow as it plays.
 */
function ThreadEmail({
  author,
  address,
  day,
  shown,
  pending,
  children,
}: {
  author: string
  address?: string
  day: string
  shown: boolean
  pending: string | null
  children: React.ReactNode
}) {
  return (
    <li className="relative">
      {pending && !shown ? <Pending label={pending} /> : null}
      <div
        className={cn(
          'grid gap-1.5 py-4 text-sm transition-[opacity,transform] duration-500 ease-out motion-reduce:transition-none',
          shown ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
        )}
      >
        <div className="flex min-w-0 items-baseline justify-between gap-3">
          <span className="min-w-0 truncate">
            <span className="font-medium">{author}</span>
            {address ? (
              <span className="text-xs text-muted-foreground">
                {' '}
                &lt;{address}&gt;
              </span>
            ) : null}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">{day}</span>
        </div>
        <p className="line-clamp-2 leading-6 text-foreground">{children}</p>
      </div>
    </li>
  )
}

/**
 * After the campaign starts, told as one conversation the way the
 * workspace's lead sheet shows it, played like a live chat once it scrolls
 * into view: the first email from an assigned mailbox, the follow-up, the
 * prospect's reply, then the AI response draft typing itself out and
 * waiting for Send. The prospect is the made-up one from the result
 * preview.
 */
export function GtmCampaignFlow() {
  const { t } = useTranslation('gtm')
  const { ref, inView } = useInView<HTMLDivElement>()
  const reducedMotion = usePrefersReducedMotion()
  const [played, setPlayed] = useState(0)
  const [typedChars, setTypedChars] = useState(0)
  const draft = t('flowDraftBody')
  const step = reducedMotion ? TIMELINE.length : played
  const typing = reached(step, 'typing')
  const typed = reducedMotion ? draft.length : typedChars
  const finished = typing && typed >= draft.length

  useEffect(() => {
    if (!inView || reducedMotion || played >= TIMELINE.length) return
    const timer = window.setTimeout(
      () => setPlayed((count) => count + 1),
      TIMELINE[played].delay,
    )
    return () => window.clearTimeout(timer)
  }, [inView, reducedMotion, played])

  useEffect(() => {
    if (!typing || reducedMotion || typedChars >= draft.length) return
    const timer = window.setTimeout(
      () => setTypedChars((count) => count + 1),
      TYPE_MS,
    )
    return () => window.clearTimeout(timer)
  }, [typing, reducedMotion, typedChars, draft.length])

  const points: Array<{ icon: LucideIcon; text: string }> = [
    { icon: Mail, text: t('flowPointMailbox') },
    { icon: Repeat, text: t('flowPointFollowUp') },
    { icon: MessagesSquare, text: t('flowPointReplies') },
  ]
  const sender = 'Jane Doe'
  const senderAddress = 'jane@acmehq.example'
  const pendingLabel = (event: TimelineEvent, label: string) =>
    reached(step, event) ? label : null

  return (
    <section className="py-20">
      <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <div>
          <h2 className="text-3xl font-display font-medium tracking-[-0.01em] text-balance text-foreground sm:text-4xl">
            {t('flowTitle')}
          </h2>
          <p className="mt-4 leading-7 text-muted-foreground">
            {t('flowText')}
          </p>
          <ul className="mt-8 divide-y divide-sidebar-border border-y border-sidebar-border">
            {points.map(({ icon: Icon, text }) => (
              <li
                key={text}
                className="flex items-center gap-3 py-3 text-foreground"
              >
                <Icon
                  className="size-4 shrink-0 text-upgrade-cta"
                  aria-hidden="true"
                />
                {text}
              </li>
            ))}
          </ul>
        </div>

        {/* An illustration; the text beside it says the same for agents. */}
        <div className="relative" data-markdown="skip">
          <div
            ref={ref}
            className="relative overflow-hidden rounded-3xl border border-sidebar-border bg-card/80"
          >
            <div className="grid gap-0.5 border-b border-sidebar-border px-5 py-3 sm:px-6">
              <div className="flex min-w-0 items-center justify-between gap-3">
                <span className="truncate font-semibold text-foreground">
                  Maya Chen
                </span>
                <span
                  className={cn(
                    'transition-opacity duration-500 motion-reduce:transition-none',
                    reached(step, 'reply') ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  <GtmStageBadge stage="interested" />
                </span>
              </div>
              <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                <span className="truncate font-mono text-foreground/80">
                  maya@northwind.example
                </span>
                <span aria-hidden="true">·</span>
                <span className="truncate">Northwind</span>
              </div>
              <p className="truncate text-sm font-medium text-foreground">
                {t('previewSubject')}
              </p>
            </div>

            <ol className="divide-y divide-sidebar-border px-5 sm:px-6">
              <ThreadEmail
                author={sender}
                address={senderAddress}
                day={t('flowMon')}
                shown={reached(step, 'first')}
                pending={pendingLabel('first_pending', t('flowSending'))}
              >
                {t('flowFirstBody')}
              </ThreadEmail>
              <ThreadEmail
                author={sender}
                address={senderAddress}
                day={t('flowThu')}
                shown={reached(step, 'follow_up')}
                pending={pendingLabel('follow_up_pending', t('flowSending'))}
              >
                {t('flowFollowUpBody')}
              </ThreadEmail>
              <ThreadEmail
                author="Maya Chen"
                day={t('flowFri')}
                shown={reached(step, 'reply')}
                pending={pendingLabel('reply_pending', t('flowIncoming'))}
              >
                {t('flowReply')}
              </ThreadEmail>
            </ol>

            <div
              className={cn(
                'grid gap-2 border-t border-sidebar-border bg-background/60 px-5 py-3 transition-opacity duration-500 motion-reduce:transition-none sm:px-6',
                reached(step, 'drafting') ? 'opacity-100' : 'opacity-0',
              )}
            >
              <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                {typing ? (
                  <Sparkles className="size-3.5" aria-hidden="true" />
                ) : (
                  <GtmSpinner className="size-3.5" />
                )}
                {typing ? t('ws_aiDraftTitle') : t('flowDrafting')}
              </p>
              <p className="relative rounded-md border border-input bg-background px-3 py-2 text-sm leading-6 text-foreground">
                <span className="sr-only">{draft}</span>
                {/* Holds the full height so typing does not resize the card. */}
                <span aria-hidden="true" className="invisible">
                  {draft}
                </span>
                <span aria-hidden="true" className="absolute inset-0 px-3 py-2">
                  {draft.slice(0, typed)}
                  {typing && !finished ? (
                    <span className="ml-px inline-block h-4 w-px translate-y-0.5 animate-pulse bg-foreground" />
                  ) : null}
                </span>
              </p>
              <div className="flex justify-end">
                <span
                  aria-hidden="true"
                  className={cn(
                    buttonVariants({ size: 'sm' }),
                    'transition-opacity duration-300 motion-reduce:transition-none',
                    finished ? 'opacity-100' : 'opacity-40',
                  )}
                >
                  <Send className="size-4" />
                  {t('ws_sendReply')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
